import { Injectable, inject } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { BillyService } from 'src/app/@pages/billyService/billy.service';
import { QuotationsService } from 'src/app/@pages/quotations/quotations.service';
import { INotas } from 'src/app/@interfaces/notas.interface';
import {
  IBillyCreditNote,
  IBillyNotesToGraphQ,
  IItemsCreditNote,
} from 'src/app/@interfaces/Billy/billy.interface';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AlertService } from '@shared-services';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class BillyNotesService {
  private quotationService = inject(QuotationsService);
  private spinnerService = inject(NgxSpinnerService);
  private billyService = inject(BillyService);
  private alertService = inject(AlertService);
  private translate = inject(TranslateService);

  private sendingInProgress = false;
  private sentNotes = new Set<string>();

  async sendDian(
    note: INotas,
  ): Promise<{ data: IBillyNotesToGraphQ; confirm: boolean } | void> {
    const noteKey = note.note_fact.toString();

    if (this.sentNotes.has(noteKey)) {
      this.showWarning(
        'This note is already being processed or was recently sent.',
      );
      return;
    }

    if (this.sendingInProgress) {
      this.showWarning(
        'A sending process is already in progress, please wait.',
      );
      return;
    }

    this.sendingInProgress = true;
    this.sentNotes.add(noteKey);
    this.spinnerService.show();

    try {
      const billyInvoice = await firstValueFrom(
        this.quotationService.getBillyInvo(noteKey),
      );

      const discrepancy = note.concepto.toString() === '2' ? '2' : '1';
      const billyNoteData = this.setNoteBilly(
        note,
        discrepancy,
        billyInvoice.invoice_number,
      );
      this.spinnerService.hide();

      const billyResponse = await firstValueFrom(
        this.billyService.postCreditNote(billyNoteData),
      );

      this.spinnerService.hide();
      this.alertService.open({
        title: this.translate.instant('global.alerts.title.success'),
        message: this.translate.instant(
          'Credit Note sent to Billy successfully.',
        ),
        type: 'success',
        acceptText: 'Ok',
      });

      return { data: billyResponse, confirm: true };
    } catch (err) {
      this.billyErrorHandler(err as HttpErrorResponse);
      throw err;
    } finally {
      this.sendingInProgress = false;
      this.sentNotes.delete(noteKey);
      this.spinnerService.hide();
    }
  }

  private setNoteBilly(
    note: INotas,
    discrepancy: string,
    invoiceNumber: string,
  ): IBillyCreditNote {
    const items: IItemsCreditNote[] = [];

    if (discrepancy === '1') {
      for (let i = 0; i < note.valores[0].codigo.length; i++) {
        const id = note.valores[0].codigo[i];
        const quantity = Math.abs(note.valores[0].cantidad[i]);
        items.push({
          type: 'item',
          id,
          attributes: { quantity },
        });
      }
    }

    return {
      data: {
        type: 'credit-note',
        attributes: {
          invoiceNumber: invoiceNumber,
          discrepancyId: discrepancy,
          items,
        },
      },
    };
  }

  private showWarning(message: string): void {
    this.alertService.open({
      title: this.translate.instant('global.alerts.title.warning'),
      message: this.translate.instant(message),
      type: 'warning',
      acceptText: 'Ok',
    });
  }

  private billyErrorHandler(error: HttpErrorResponse): void {
    this.spinnerService.hide();
    this.billyService.handleError(error);
  }
}
